import { h } from 'preact';
import { useState, useRef, useEffect } from 'preact/hooks';
import { t, translateError } from '../i18n.js';
import { login, verifyMfa } from '../state/auth.js';
import { loadTheme } from '../state/theme.js';
import { connectSocket, loadNotifications } from '../state/notifications.js';
import { route } from 'preact-router';
import LanguageSwitcher from '../components/shared/LanguageSwitcher.jsx';

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const REMEMBER_KEY = 'sportapp-remember-email';

const SPORTS = [
  { emoji: '⚽', label: 'Voetbal' },
  { emoji: '🏀', label: 'Basketball' },
  { emoji: '🏐', label: 'Volleyball' },
  { emoji: '🎾', label: 'Tennis' },
  { emoji: '🏊', label: 'Zwemmen' },
  { emoji: '🚴', label: 'Wielrennen' },
  { emoji: '🏑', label: 'Hockey' },
  { emoji: '🤾', label: 'Handbal' },
  { emoji: '⛳', label: 'Golf' },
  { emoji: '🏓', label: 'Tafeltennis' },
  { emoji: '🤸', label: 'Turnen' },
  { emoji: '🥊', label: 'Boksen' },
  { emoji: '🏋️', label: 'Fitness' },
  { emoji: '🎿', label: 'Skiën' },
  { emoji: '🏇', label: 'Paardrijden' },
  { emoji: '🏸', label: 'Badminton' },
];

function SportPanel() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    let items = [];

    function init() {
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      const dpr = window.devicePixelRatio || 1;
      canvas.width = w * dpr;
      canvas.height = h * dpr;
      const ctx = canvas.getContext('2d');
      ctx.scale(dpr, dpr);

      items = SPORTS.map(s => ({
        emoji: s.emoji,
        x: Math.random() * w,
        y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.35,
        vy: (Math.random() - 0.5) * 0.35,
        size: 30 + Math.random() * 32,
        opacity: 0.07 + Math.random() * 0.16,
        rotation: Math.random() * Math.PI * 2,
        rotSpeed: (Math.random() - 0.5) * 0.008,
      }));
    }

    init();

    let animId;

    function draw() {
      const canvas = canvasRef.current;
      if (!canvas) return;
      const ctx = canvas.getContext('2d');
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      ctx.clearRect(0, 0, w, h);

      items.forEach(item => {
        ctx.save();
        ctx.globalAlpha = item.opacity;
        ctx.translate(item.x, item.y);
        ctx.rotate(item.rotation);
        ctx.font = `${item.size}px serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(item.emoji, 0, 0);
        ctx.restore();

        item.x += item.vx;
        item.y += item.vy;
        item.rotation += item.rotSpeed;

        if (item.x < -60) item.x = w + 60;
        if (item.x > w + 60) item.x = -60;
        if (item.y < -60) item.y = h + 60;
        if (item.y > h + 60) item.y = -60;
      });

      animId = requestAnimationFrame(draw);
    }

    draw();

    const ro = new ResizeObserver(() => init());
    ro.observe(canvas);

    return () => {
      cancelAnimationFrame(animId);
      ro.disconnect();
    };
  }, []);

  return (
    <div style="position:relative;flex:1;background:linear-gradient(145deg,#1a2d5a 0%,#0d1b38 100%);display:flex;flex-direction:column;align-items:flex-start;justify-content:center;padding:56px 52px;overflow:hidden">
      <canvas ref={canvasRef} style="position:absolute;inset:0;width:100%;height:100%;pointer-events:none" />

      <div style="position:relative;z-index:1;max-width:400px">
        {/* Wordmark */}
        <div style="font-size:20px;font-weight:800;color:#fff;margin-bottom:52px;letter-spacing:-0.3px">
          Vereniging<span style="color:#f15c1e">4U</span>
        </div>

        {/* Tagline */}
        <div style="font-size:50px;font-weight:800;color:#fff;line-height:1.08;letter-spacing:-1.5px;margin-bottom:4px">
          Minder chaos,
        </div>
        <div style="font-size:50px;font-weight:800;color:#f15c1e;line-height:1.08;letter-spacing:-1.5px;font-style:italic;margin-bottom:28px">
          meer club.
        </div>

        <p style="font-size:16px;color:rgba(255,255,255,0.6);line-height:1.65;margin-bottom:44px;max-width:340px">
          Eén platform voor processen, taken en seizoensplanning — voor elke sportvereniging.
        </p>

        {/* Sport pills */}
        <div>
          <div style="font-size:10px;font-weight:600;text-transform:uppercase;letter-spacing:.12em;color:rgba(255,255,255,0.3);margin-bottom:14px">
            Geschikt voor alle sporten
          </div>
          <div style="display:flex;flex-wrap:wrap;gap:8px">
            {SPORTS.slice(0, 11).map(s => (
              <div key={s.label} style="display:inline-flex;align-items:center;gap:7px;height:34px;padding:0 14px;border-radius:34px;background:rgba(255,255,255,0.08);border:1px solid rgba(255,255,255,0.14);backdrop-filter:blur(10px);white-space:nowrap">
                <span style="font-size:15px;line-height:1">{s.emoji}</span>
                <span style="font-size:12px;font-weight:500;color:rgba(255,255,255,0.82);letter-spacing:.01em">{s.label}</span>
              </div>
            ))}
            <div style="display:inline-flex;align-items:center;height:34px;padding:0 16px;border-radius:34px;background:rgba(241,92,30,0.2);border:1px solid rgba(241,92,30,0.4)">
              <span style="font-size:12px;font-weight:600;color:#f47c50;letter-spacing:.02em">& meer →</span>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom attribution */}
      <div style="position:absolute;bottom:24px;left:52px;font-size:11px;color:rgba(255,255,255,0.25);z-index:1">
        © 2026 Vereniging4U · <a href="https://www.vereniging4u.nl" style="color:rgba(255,255,255,0.35);text-decoration:none">vereniging4u.nl</a>
      </div>
    </div>
  );
}

export default function LoginView() {
  const [email, setEmail] = useState(() => localStorage.getItem(REMEMBER_KEY) || '');
  const [password, setPassword] = useState('');
  const [mfaCode, setMfaCode] = useState('');
  const [showMfa, setShowMfa] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberEmail, setRememberEmail] = useState(() => !!localStorage.getItem(REMEMBER_KEY));
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const emailRef = useRef(null);
  const passwordRef = useRef(null);
  const mfaRef = useRef(null);

  useEffect(() => {
    if (showMfa && mfaRef.current) mfaRef.current.focus();
  }, [showMfa]);

  const validate = () => {
    if (!email.trim()) return t('auth.errors.email_required');
    if (!EMAIL_REGEX.test(email.trim())) return t('auth.errors.invalid_email_format');
    if (!password) return t('auth.errors.password_required');
    return null;
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      if (!email.trim() || !EMAIL_REGEX.test(email.trim())) {
        emailRef.current?.focus();
      } else {
        passwordRef.current?.focus();
      }
      return;
    }

    setError('');
    setLoading(true);

    if (rememberEmail) {
      localStorage.setItem(REMEMBER_KEY, email.trim());
    } else {
      localStorage.removeItem(REMEMBER_KEY);
    }

    try {
      const data = await login(email, password);
      if (data.requireMfa) {
        setShowMfa(true);
        setLoading(false);
        return;
      }
      await postLogin(data);
    } catch (err) {
      setError(translateError(err.code, err.detail));
      setLoading(false);
      if (err.code === 'invalid_credentials') {
        passwordRef.current?.focus();
      } else {
        emailRef.current?.focus();
      }
    }
  };

  const handleMfa = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const data = await verifyMfa(mfaCode);
      await postLogin(data);
    } catch (err) {
      setError(translateError(err.code, err.detail));
      setMfaCode('');
      setLoading(false);
      // Too late or the step was lost: back to e-mail and password
      if (err.code === 'mfa_expired' || err.code === 'mfa_required') setShowMfa(false);
      else mfaRef.current?.focus();
    }
  };

  const postLogin = async (data) => {
    if (data.activeClubId) {
      await loadTheme();
      connectSocket();
      loadNotifications();
      route('/dashboard');
    } else {
      route('/select-club');
    }
  };

  return (
    <div class="login-split">
      {/* Left — sports panel */}
      <div class="login-panel-left">
        <SportPanel />
      </div>

      {/* Right — form */}
      <div class="login-panel-right">
        <div style="position:absolute;top:20px;right:20px">
          <LanguageSwitcher />
        </div>

        <div class="login-card">
          {/* Logo */}
          <div style="width:52px;height:52px;background:#1a2d5a;border-radius:14px;display:flex;align-items:center;justify-content:center;margin:0 auto 20px;font-size:22px;font-weight:900;color:#fff;font-family:var(--font-display)">
            V
          </div>
          <h1 class="login-title">{t('auth.loginTitle')}</h1>
          <p class="login-subtitle">{t('auth.loginSubtitle')}</p>

          {error && <div class="alert alert-error">{error}</div>}

          {!showMfa ? (
            <form onSubmit={handleLogin} noValidate>
              <div class="form-group">
                <label class="form-label">{t('auth.email')}</label>
                <input
                  ref={emailRef}
                  class="form-input"
                  type="email"
                  value={email}
                  onInput={(e) => setEmail(e.target.value)}
                  autocomplete="email"
                />
              </div>

              <div class="form-group">
                <div style="display:flex;justify-content:space-between;align-items:baseline">
                  <label class="form-label">{t('auth.password')}</label>
                  <a
                    href="/forgot-password"
                    style="font-size:12px;color:var(--color-primary);text-decoration:none"
                    onClick={(e) => { e.preventDefault(); route('/forgot-password'); }}
                  >
                    {t('auth.forgotPassword')}
                  </a>
                </div>
                <div style="position:relative">
                  <input
                    ref={passwordRef}
                    class="form-input"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onInput={(e) => setPassword(e.target.value)}
                    style="padding-right:42px"
                    autocomplete="current-password"
                  />
                  <button
                    type="button"
                    aria-label={showPassword ? t('auth.hidePassword') : t('auth.showPassword')}
                    onClick={() => setShowPassword(v => !v)}
                    style="position:absolute;right:10px;top:50%;transform:translateY(-50%);background:none;border:none;cursor:pointer;padding:4px;color:var(--color-text-muted);display:flex;align-items:center"
                  >
                    {showPassword ? (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94"/>
                        <path d="M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/>
                        <line x1="1" y1="1" x2="23" y2="23"/>
                      </svg>
                    ) : (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                        <circle cx="12" cy="12" r="3"/>
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <div style="display:flex;align-items:center;gap:8px;margin-bottom:16px">
                <input
                  type="checkbox"
                  id="remember-email"
                  checked={rememberEmail}
                  onChange={(e) => setRememberEmail(e.target.checked)}
                  style="width:16px;height:16px;cursor:pointer;accent-color:var(--color-primary)"
                />
                <label for="remember-email" style="font-size:14px;color:var(--color-text-muted);cursor:pointer;user-select:none">
                  {t('auth.rememberEmail')}
                </label>
              </div>

              <button class="btn btn-primary btn-lg" style="width:100%;display:flex;align-items:center;justify-content:center;gap:8px;background:#1a2d5a;border-color:#1a2d5a" type="submit" disabled={loading}>
                {loading && <span class="btn-spinner" />}
                {loading ? t('common.loading') : t('auth.login')}
              </button>
            </form>
          ) : (
            <form onSubmit={handleMfa}>
              <h3 class="mb-2" style="font-size:16px">{t('auth.mfaTitle')}</h3>
              <p class="text-muted text-sm mb-4">{t('auth.mfaSubtitle')}</p>
              <div class="form-group">
                <label class="form-label">{t('auth.mfaCode')}</label>
                <input
                  ref={mfaRef}
                  class="form-input"
                  type="text"
                  inputMode="text"
                  value={mfaCode}
                  onInput={(e) => setMfaCode(e.target.value)}
                  autocomplete="one-time-code"
                  maxLength={9}
                />
                <div class="text-sm text-muted" style="margin-top:4px">Telefoon niet bij de hand? Vul een van je herstelcodes in (bijv. abcd-efgh).</div>
              </div>
              <button class="btn btn-primary btn-lg" style="width:100%;display:flex;align-items:center;justify-content:center;gap:8px;background:#1a2d5a;border-color:#1a2d5a" type="submit" disabled={loading}>
                {loading && <span class="btn-spinner" />}
                {loading ? t('common.loading') : t('auth.verifyMfa')}
              </button>
            </form>
          )}

          <p class="login-footer">{t('auth.noAccount')}</p>
        </div>
      </div>
    </div>
  );
}
